import { useEffect, useMemo, useRef, useState } from "react";
import { Search, X, Volume2, BookOpen } from "lucide-react";
import { getWords, getCategories } from "@/lib/api";
import { Ornament } from "@/components/Ornament";

const PAGE = 60;

export default function Dictionary() {
  const [words, setWords] = useState([]);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("Tümü");
  const [letter, setLetter] = useState("");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [visible, setVisible] = useState(PAGE);
  const folioRef = useRef(null);

  useEffect(() => {
    getCategories().then(setCategories).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (q) params.q = q;
    if (category && category !== "Tümü") params.category = category;
    if (letter) params.letter = letter;
    const t = setTimeout(() => {
      getWords(params)
        .then((data) => {
          setWords(data);
          setVisible(PAGE);
          setSelected((prev) => prev || data[0] || null);
        })
        .finally(() => setLoading(false));
    }, 180);
    return () => clearTimeout(t);
  }, [q, category, letter]);

  const alphabet = useMemo(
    () => ["A", "B", "C", "Ç", "D", "E", "F", "G", "H", "I", "İ", "K", "L", "M",
           "N", "O", "Ö", "P", "R", "S", "Ş", "T", "U", "Ü", "X", "Y", "Z"],
    []
  );

  const speak = (text) => {
    try {
      const u = new SpeechSynthesisUtterance(text);
      u.lang = "tr-TR";
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(u);
    } catch (e) {}
  };

  const selectWord = (w) => {
    setSelected(w);
    if (window.innerWidth < 1024 && folioRef.current) {
      folioRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const clearFilters = () => {
    setQ("");
    setCategory("Tümü");
    setLetter("");
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="text-center max-w-2xl mx-auto">
        <span className="eyebrow">Aranabilir Sözlük</span>
        <h1 className="mt-3 font-[Cinzel] text-4xl sm:text-5xl font-bold text-foreground">Kelime Hazinesi</h1>
        <p className="mt-4 text-foreground/75">
          Kelimeyi, anlamını ya da okunuşunu yazarak arayın; harf ve konuya göre süzün.
        </p>
        <div className="flex justify-center text-accent mt-6">
          <Ornament className="w-40 h-5" />
        </div>
      </div>

      {/* Search + filters */}
      <div className="mt-8 space-y-5">
        <div className="relative max-w-2xl mx-auto">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <input
            data-testid="dictionary-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Örn: alp, bilge, kut, düşman…"
            className="w-full rounded-full border border-border bg-card pl-12 pr-11 py-3.5 text-base outline-none focus:border-accent focus:ring-2 focus:ring-accent/30 transition-colors"
          />
          {q && (
            <button
              onClick={() => setQ("")}
              data-testid="search-clear-btn"
              className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label="Temizle"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="flex flex-wrap justify-center gap-2" data-testid="alphabet-filter-bar">
          {alphabet.map((a) => (
            <button
              key={a}
              onClick={() => setLetter((l) => (l === a ? "" : a))}
              data-testid={`letter-${a}`}
              className={`w-9 h-9 rounded-md font-[Cinzel] text-sm border transition-colors ${
                letter === a
                  ? "bg-primary text-primary-foreground border-primary"
                  : "border-border bg-card text-foreground/70 hover:border-accent"
              }`}
            >
              {a}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap justify-center gap-2" data-testid="category-filter-select">
          {["Tümü", ...categories].map((c) => (
            <button
              key={c}
              onClick={() => setCategory(c)}
              data-testid={`category-${c}`}
              className={`px-4 py-1.5 rounded-full text-sm border transition-colors ${
                category === c
                  ? "bg-accent text-accent-foreground border-accent"
                  : "border-border bg-card text-foreground/70 hover:border-accent"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      {/* List + folio */}
      <div className="mt-10 grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-5" data-testid="word-list-container">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-muted-foreground">
              {loading ? "Yükleniyor…" : `${words.length} madde`}
            </p>
            {(q || letter || category !== "Tümü") && (
              <button onClick={clearFilters} className="text-sm rubric hover:underline">
                Filtreleri temizle
              </button>
            )}
          </div>

          {!loading && words.length === 0 && (
            <div className="rounded-lg border border-border bg-card p-8 text-center text-muted-foreground">
              Aradığınız ölçütlere uygun kelime bulunamadı.
            </div>
          )}

          <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
            {words.slice(0, visible).map((w) => (
              <button
                key={w.id}
                onClick={() => selectWord(w)}
                data-testid={`word-card-${w.id}`}
                className={`card-hover w-full text-left rounded-lg border bg-card p-4 flex items-start gap-3 ${
                  selected?.id === w.id ? "border-accent ring-1 ring-accent/40" : "border-border"
                }`}
              >
                <span className="mt-1.5 w-2 h-2 rounded-full bg-primary shrink-0" />
                <span className="flex-1">
                  <span className="flex items-baseline gap-2">
                    <span className="font-[Cinzel] text-xl font-semibold text-foreground">{w.word}</span>
                    {w.gokturk && <span className="gokturk text-accent text-sm">{w.gokturk}</span>}
                  </span>
                  <span className="block text-sm text-foreground/70 line-clamp-2 mt-0.5">{w.meaning}</span>
                  <span className="mt-2 inline-block text-[0.65rem] font-mono uppercase tracking-wider text-muted-foreground border border-border rounded px-2 py-0.5">
                    {w.category}
                  </span>
                </span>
              </button>
            ))}
            {words.length > visible && (
              <button
                onClick={() => setVisible((v) => v + PAGE)}
                data-testid="word-list-load-more"
                className="w-full rounded-lg border border-dashed border-accent py-3 text-sm rubric hover:bg-accent/10 transition-colors"
              >
                Daha fazla göster ({words.length - visible} madde daha)
              </button>
            )}
          </div>
        </div>

        {/* Folio detail */}
        <div className="lg:col-span-7" ref={folioRef}>
          <div className="lg:sticky lg:top-24">
            {selected ? (
              <article
                key={selected.id}
                data-testid="word-detail-folio"
                className="folio-frame rounded-lg bg-card p-7 sm:p-10 animate-ink"
              >
                <div className="flex items-center justify-between">
                  <span className="eyebrow">Varak · {selected.category}</span>
                  <button
                    onClick={() => speak(selected.pronunciation)}
                    data-testid="folio-audio-btn"
                    aria-label="Telaffuzu dinle"
                    className="flex items-center justify-center w-9 h-9 rounded-full border border-accent text-accent hover:bg-accent/10 transition-colors"
                  >
                    <Volume2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="gold-rule my-5" />
                <div className="flex items-end gap-4 flex-wrap">
                  <h2 className="font-[Cinzel] text-5xl font-bold text-foreground">{selected.word}</h2>
                  {selected.gokturk && (
                    <span data-testid="word-gokturk-script" className="gokturk text-4xl text-accent pb-1">
                      {selected.gokturk}
                    </span>
                  )}
                </div>
                <p className="mt-2 font-mono text-sm text-muted-foreground">/{selected.pronunciation}/</p>

                <p className={`mt-6 text-lg leading-relaxed text-foreground/90 ${selected.meaning.length > 80 ? "drop-cap" : ""}`}>{selected.meaning}</p>

                {(selected.etymology || selected.example) && (
                  <div className="mt-6 grid sm:grid-cols-2 gap-4">
                    {selected.etymology && (
                      <div className="rounded-md border border-border p-4" data-testid="word-etymology-tag">
                        <p className="eyebrow mb-2">Köken</p>
                        <p className="text-sm text-foreground/80 leading-relaxed">{selected.etymology}</p>
                      </div>
                    )}
                    {selected.example && (
                      <div className="rounded-md border border-border p-4">
                        <p className="eyebrow mb-2">Örnek Kullanım</p>
                        <p className="gokturk text-base text-foreground">“{selected.example}”</p>
                        <p className="text-sm text-muted-foreground mt-1">{selected.example_meaning}</p>
                      </div>
                    )}
                  </div>
                )}

                {selected.source && (
                  <p className="mt-6 text-sm font-mono text-muted-foreground" data-testid="word-source">
                    {selected.source}
                  </p>
                )}
                {selected.kasgari_quote && (
                  <blockquote className="mt-6 border-l-2 border-primary pl-4 italic text-foreground/75">
                    {selected.kasgari_quote}
                  </blockquote>
                )}
              </article>
            ) : (
              <div className="folio-frame rounded-lg bg-card p-12 text-center text-muted-foreground">
                <BookOpen className="w-8 h-8 mx-auto mb-4 text-accent" />
                İncelemek için soldan bir kelime seçin.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
